import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { getRuntimeConfigEntry, setRuntimeConfig } from "@/lib/api";

const GAME_VERSIONS_KEY = "allowed_game_versions";

function parseGameVersions(value: string | undefined): string[] {
  if (!value?.trim()) return [];
  const parsed: unknown = JSON.parse(value);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed) || !Array.isArray((parsed as { versions?: unknown }).versions) || (parsed as { versions: unknown[] }).versions.some((version) => typeof version !== "string")) {
    throw new Error('Stored game versions must have a string list in "versions".');
  }
  return (parsed as { versions: string[] }).versions;
}

export function GameVersionsRoute() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["runtime-config-entry", GAME_VERSIONS_KEY],
    queryFn: async () => parseGameVersions((await getRuntimeConfigEntry(GAME_VERSIONS_KEY))?.value),
  });
  const [versions, setVersions] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!initialized && query.isSuccess) {
      setVersions(query.data ?? []);
      setInitialized(true);
    }
  }, [initialized, query.isSuccess, query.data]);

  const save = useMutation({
    mutationFn: () => {
      const next = versions.map((version) => version.trim());
      if (next.some((version) => !version)) throw new Error("Every game version must have a value.");
      if (new Set(next).size !== next.length) throw new Error("Each game version may appear only once.");
      return setRuntimeConfig(GAME_VERSIONS_KEY, JSON.stringify({ versions: next }));
    },
    onSuccess: () => {
      setVersions((current) => current.map((version) => version.trim()));
      queryClient.invalidateQueries({ queryKey: ["runtime-config-entry", GAME_VERSIONS_KEY] });
    },
  });

  const storedVersions = query.data ?? [];
  const dirty = JSON.stringify(versions) !== JSON.stringify(storedVersions);

  return (
    <AppShell title="Game Versions" subtitle="Only listed client versions can access and save game data.">
      <div className="max-w-2xl space-y-5">
        <Alert>
          <AlertDescription className="text-xs">
            Removing a version blocks that client from accessing data. Add a new version before releasing it.
          </AlertDescription>
        </Alert>

        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <div className="px-4 py-2 border-b border-border/60 flex items-center justify-between gap-3">
            <h3 className="text-sm font-medium">Allowed versions</h3>
            <span className="text-xs text-muted-foreground">
              {query.isLoading ? "" : `${versions.length} version${versions.length === 1 ? "" : "s"}`}
            </span>
          </div>
          <div className="p-4 space-y-3">
            {query.isLoading ? <Skeleton className="h-24 w-full" /> : query.isError ? (
              <Alert variant="destructive"><AlertDescription>Failed to load game versions: {String(query.error)}</AlertDescription></Alert>
            ) : <>
              {versions.length === 0 && <p className="text-sm text-muted-foreground">No versions are allowed.</p>}
              {versions.map((version, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    aria-label={`Game version ${index + 1}`}
                    value={version}
                    onChange={(event) => setVersions((current) => current.map((value, i) => i === index ? event.target.value : value))}
                    placeholder="e.g. 2.2.0"
                  />
                  <Button type="button" variant="destructive" size="icon" onClick={() => setVersions((current) => current.filter((_, i) => i !== index))} title="Remove version">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              <Button type="button" variant="secondary" size="sm" onClick={() => setVersions((current) => [...current, ""])}>
                <Plus className="h-3.5 w-3.5" /> Add version
              </Button>
              <div className="flex items-center gap-2 pt-2 border-t border-border/60">
                <Button size="sm" onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
                  {save.isPending ? "Saving…" : "Save changes"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setVersions(storedVersions); save.reset(); }} disabled={!dirty || save.isPending}>Reset</Button>
                {save.isSuccess && !dirty && <Badge variant="success" className="text-[10px]">Saved</Badge>}
              </div>
              {save.error && <Alert variant="destructive"><AlertDescription>{String(save.error)}</AlertDescription></Alert>}
            </>}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
